import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useTranslationTokens } from '@frontend/i18n-tokens';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { AvailabilityMode } from '../../../../components/AvailabilityRules';
import { NoProvidersIllustration } from '../../../../images';

type Props = {
  availabilityMode: AvailabilityMode;
  hasSearchValue?: boolean;
};

export const AvailabilityRuleEmptyState = ({ availabilityMode, hasSearchValue }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const tokens = useTranslationTokens();
  const { navigate } = useSettingsNavigate();

  const handleActionClick = () => {
    navigate({ to: '/schedule/operatories' });
  };

  if (hasSearchValue) {
    return (
      <section css={containerStyles}>
        {/* TODO: Update empty state illustration for search */}
        <NoProvidersIllustration width={336} height={336} />
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginTop: theme.spacing(3),
            marginBottom: theme.spacing(3),
          }}
        >
          <Text textAlign='center' color='subdued'>
            {t('No results found')}
          </Text>
          <Text textAlign='center' color='subdued'>
            {t('Try adjusting your search terms')}
          </Text>
        </div>
      </section>
    );
  }

  return (
    <section css={containerStyles}>
      <NoProvidersIllustration width={336} height={336} />
      <Heading as='h3' textAlign='center' css={{ fontSize: theme.fontSize(20), marginTop: theme.spacing(3) }}>
        {availabilityMode === 'OPERATORY' && t('No {{operatories}} found', { operatories: t(tokens.operatories) })}
        {availabilityMode === 'PROVIDER' && t('No providers found')}
      </Heading>
      <Text textAlign='center' color='subdued'>
        {availabilityMode === 'OPERATORY' &&
          t("To add availability, {{operatories}} must be mapped and added to your organization's locations.", {
            operatories: tokens.operatories,
          })}
        {availabilityMode === 'PROVIDER' &&
          t("To add availability, providers must be mapped and added to your organization's locations.")}
      </Text>
      <Button variant='primary' onClick={handleActionClick} css={{ marginTop: theme.spacing(2) }}>
        {availabilityMode === 'OPERATORY' && t('{{operatory}} Settings', { operatory: tokens.operatory })}
        {availabilityMode === 'PROVIDER' && t('Provider Settings')}
      </Button>
    </section>
  );
};

const containerStyles = css({
  maxWidth: 350,
  justifySelf: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1),
});
